import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTicketsStore } from 'src/stores/tickets'
import { useApprovalsStore } from 'src/stores/approvals'
import { useConfirmationsStore } from 'src/stores/confirmations'
import { usePoliciesStore } from 'src/stores/policies'
import { useTicketStore } from 'src/stores/ticket'
import { useApprovalStore } from 'src/stores/approval'
import { useAccountStore } from 'src/stores/account'
import { useMetadataStore } from 'src/stores/account/metadata'
import { ROUTES } from 'src/router/const'
import { ROUTES_METADATA, hasQueryParamOverlay } from 'src/config/routesMetadata.config.js'

export function useDynamicMetaData() {
  const route = useRoute()
  const metadataStore = useMetadataStore()

  // Extract dynamic data for metadata generation
  const dynamicData = computed(() => {
    // Priority 1: Check for query param metadata (drawers/modals)
    if (route.query?.approvalId) {
      const approvalStore = useApprovalStore()
      const approval = approvalStore.approval
      if (approval && !approvalStore.isApprovalLoading) {
        return {
          productName: approval.productName,
          companyName: approval.companyName,
        }
      }
      // Return empty if still loading
      return {}
    }

    if (route.query?.ticketId) {
      const ticketStore = useTicketStore()
      const ticket = ticketStore.ticket
      if (ticket && !ticketStore.isTicketLoading) {
        return {
          productName: ticket.productName,
          companyName: ticket.companyName,
        }
      }
      // Return empty if still loading
      return {}
    }

    // Priority 2: Route-based dynamic data
    const accountStore = useAccountStore()
    if (!accountStore.accountData) {
      // Data not loaded yet
      return {}
    }

    switch (route.name) {
      case ROUTES.TICKETS: {
        const ticketsStore = useTicketsStore()
        const count = ticketsStore.directoryCounts[ticketsStore.filterBy.status] || 0
        return { count }
      }

      case ROUTES.APPROVALS: {
        const approvalsStore = useApprovalsStore()
        const count = approvalsStore.directoryCounts[approvalsStore.filterBy.status] || 0
        return { count }
      }

      case ROUTES.CONFIRMATIONS: {
        const confirmationsStore = useConfirmationsStore()
        const count = confirmationsStore.totalRequiringConfirmation || 0
        return { count }
      }

      case ROUTES.POLICIES: {
        const policiesStore = usePoliciesStore()
        const count = policiesStore.directoryCounts[policiesStore.activeType] || 0
        // activeType, not filterBy.status: the store falls back to the first published list, so a
        // bare or stale ?status= would otherwise title the page after a list it never rendered.
        return { count, status: policiesStore.activeType }
      }

      default:
        break
    }

    return {}
  })

  let lastSentData = null
  let lastSentRoute = null

  watch(
    dynamicData,
    () => {
      const data = dynamicData.value
      const routePath = route.fullPath

      const shouldHandleRoute =
        hasQueryParamOverlay(route) || ROUTES_METADATA[route.name]?.skipMetadataGeneration

      // If the route is not a dynamic route, reset the last sent data and route
      if (!shouldHandleRoute) {
        lastSentRoute = null
        lastSentData = null
        return
      }

      if (Object.keys(data).length === 0) return

      const routeChanged = routePath !== lastSentRoute
      const dataChanged = JSON.stringify(data) !== JSON.stringify(lastSentData)

      // Update metadata if route changed OR data changed (including count updates from mutations)
      if (routeChanged || dataChanged) {
        lastSentData = JSON.parse(JSON.stringify(data))
        lastSentRoute = routePath
        metadataStore.generateMetadata(data)
      }
    },
    { immediate: true, deep: true }
  )

  return { dynamicData }
}
